import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';
import { CogIcon, TicketIcon, CreditCardIcon, UsersIcon, KeyIcon, DocumentMagnifyingGlassIcon, ArrowPathIcon, ChartBarIcon, BeakerIcon } from '@heroicons/react/24/outline';
import CouponManagement from '../components/admin/CouponManagement';
import AccessCodeManagement from '../components/admin/AccessCodeManagement';
import AccessGrantsManager from '../components/admin/AccessGrantsManager';
import ReactivationDashboard from '../components/admin/ReactivationDashboard';
import AccountsAndSubscriptions from '../components/admin/AccountsAndSubscriptions';

// NO HARDCODED ADMINS - Use database as single source of truth
// This follows the "fail loud" policy - no fallbacks that hide issues

// System administrators (temporary for fixing broken components)
const SYSTEM_ADMINS = [
  'john@flowlogicllc.com'
];

interface TabConfig {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType;
}

export default function AccountAdminPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('accounts');
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, [user]);

  const checkAuthorization = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // TEMPORARY: Hardcode your email to bypass the broken RLS check
    // TODO: Fix the RLS policies on system_admins table
    if (user.email === 'john@flowlogicllc.com') {
      console.log('Admin access granted for john@flowlogicllc.com (temporary bypass)');
      setIsAuthorized(true);
      setLoading(false);
      return;
    }

    // ONLY check the system_admins table - single source of truth
    try {
      const { data: systemAdmin, error } = await supabase
        .from('system_admins')
        .select('email')
        .eq('email', user.email)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error checking admin status:', error);
        // FAIL LOUD - don't hide the error
        alert('Error checking admin permissions. Please contact support.');
        navigate('/dashboard');
        return;
      }

      if (systemAdmin) {
        setIsAuthorized(true);
      } else {
        console.log('User not authorized for admin console:', user.email);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to check admin authorization:', error);
      // FAIL LOUD
      alert('Failed to verify admin permissions. Please contact support.');
      navigate('/dashboard');
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar onSignOut={signOut} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar onSignOut={signOut} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 p-8 rounded-lg text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Access Denied</h2>
            <p className="text-red-600">You don't have permission to access Account Admin.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs: TabConfig[] = [
    {
      id: 'accounts',
      label: 'Accounts & Subscriptions',
      icon: UsersIcon,
      component: AccountsAndSubscriptions
    },
    {
      id: 'access-codes',
      label: 'Access Codes',
      icon: KeyIcon,
      component: AccessCodeManagement
    },
    {
      id: 'access-grants',
      label: 'Access Grants',
      icon: DocumentMagnifyingGlassIcon,
      component: AccessGrantsManager
    },
    {
      id: 'reactivation',
      label: 'Reactivation',
      icon: ArrowPathIcon,
      component: ReactivationDashboard
    },
    {
      id: 'coupons',
      label: 'Stripe Coupons',
      icon: TicketIcon,
      component: CouponManagement
    },
    {
      id: 'users',
      label: 'System Admins',
      icon: CogIcon,
      component: SystemUserManagement
    },
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || AccessCodeManagement;

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={signOut} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Account Admin
          </h1>
          <p className="text-gray-600 mt-2">Manage accounts, access codes, and subscriptions</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white shadow-sm rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Active Tab Content */}
        <div className="bg-white shadow rounded-lg">
          <ActiveComponent />
        </div>
      </div>
    </div>
  );
}

// Old Coupon Management Component - replaced by CouponManagement component
function OldCouponManagement() {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: 'Beta-',
    description: '',
    discount_percent: 100,
    duration_months: 3,
    max_redemptions: 1,
    expires_at: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await fetch('/api/admin/coupons', {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Coupon API response:', data);
        setCoupons(data.coupons || []);
        
        // Show debug info if available
        if (data._debug) {
          console.log('Debug info:', data._debug);
        }
      } else {
        let errorMessage = 'Unknown error';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            console.error('API error response:', errorData);
            errorMessage = errorData.error || 'Failed to load coupons';
            if (errorData.details) {
              errorMessage += `\n\n${errorData.details}`;
            }
          } else {
            // Not JSON, probably HTML error page
            errorMessage = `Server error (${response.status}). The Stripe API key may not be configured.`;
          }
        } catch (parseError) {
          console.error('Failed to parse error response');
          errorMessage = `Server error (${response.status})`;
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      alert('Failed to connect to the server. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    try {
      const response = await fetch('/api/admin/coupons/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(newCoupon)
      });

      if (response.ok) {
        await fetchCoupons();
        setShowCreateForm(false);
        setNewCoupon({
          code: 'Beta-',
          description: '',
          discount_percent: 100,
          duration_months: 3,
          max_redemptions: 1,
          expires_at: ''
        });
      } else {
        const errorData = await response.json();
        console.error('Error creating coupon:', errorData);
        alert(`Failed to create coupon: ${errorData.details || errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      alert('Failed to create coupon. Please check your connection and try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Beta Invitation Coupons</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create New Coupon
        </button>
      </div>

      {showCreateForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium mb-4">Create New Beta Coupon</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coupon Code
              </label>
              <input
                type="text"
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Beta-CompanyName"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newCoupon.description}
                onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="For Acme Corp beta testing"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount %
              </label>
              <input
                type="number"
                value={newCoupon.discount_percent}
                onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (months)
              </label>
              <input
                type="number"
                value={newCoupon.duration_months || ''}
                onChange={(e) => setNewCoupon({ ...newCoupon, duration_months: e.target.value ? parseInt(e.target.value) : 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Redemptions
              </label>
              <input
                type="number"
                value={newCoupon.max_redemptions}
                onChange={(e) => setNewCoupon({ ...newCoupon, max_redemptions: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expires On
              </label>
              <input
                type="date"
                value={newCoupon.expires_at}
                onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={createCoupon}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Create Coupon
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Coupons created here are automatically added to your Stripe account. 
          Only coupons starting with "Beta" (case-insensitive) will appear in this list.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Discount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Used / Max
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {coupons.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No coupons found. Create one in Stripe first, then track it here.
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {coupon.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {coupon.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {coupon.discount_percent}% off
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {coupon.times_redeemed} / {coupon.max_redemptions || '∞'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      coupon.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {coupon.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button className="text-blue-600 hover:text-blue-900">
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Subscription Management Component
function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const { data } = await supabase
        .from('accounts')
        .select(`
          id,
          name,
          email,
          subscription_status,
          subscription_plan,
          trial_end_date,
          is_beta_tester,
          beta_access_end_date,
          created_at
        `)
        .order('created_at', { ascending: false });

      setSubscriptions(data || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const extendTrial = async (accountId: string, days: number) => {
    try {
      const { data: account } = await supabase
        .from('accounts')
        .select('trial_end_date')
        .eq('id', accountId)
        .single();

      const currentEndDate = account?.trial_end_date ? new Date(account.trial_end_date) : new Date();
      const newEndDate = new Date(currentEndDate);
      newEndDate.setDate(newEndDate.getDate() + days);

      await supabase
        .from('accounts')
        .update({ trial_end_date: newEndDate.toISOString() })
        .eq('id', accountId);

      await fetchSubscriptions();
    } catch (error) {
      console.error('Error extending trial:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription Management</h2>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trial/Beta Ends
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subscriptions.map((sub) => (
              <tr key={sub.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{sub.name}</div>
                    <div className="text-sm text-gray-500">{sub.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {sub.subscription_plan || 'None'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    sub.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                    sub.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {sub.subscription_status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {sub.trial_end_date ? new Date(sub.trial_end_date).toLocaleDateString() :
                   sub.beta_access_end_date ? new Date(sub.beta_access_end_date).toLocaleDateString() :
                   'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {sub.is_beta_tester ? (
                    <span className="text-purple-600 font-medium">Beta Tester</span>
                  ) : (
                    'Regular'
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => extendTrial(sub.id, 30)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    +30 days
                  </button>
                  <button className="text-blue-600 hover:text-blue-900">
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// System User Management Component
function SystemUserManagement() {
  const { user } = useAuth();
  const [systemAdmins, setSystemAdmins] = useState<string[]>(SYSTEM_ADMINS);
  const [newAdmin, setNewAdmin] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSystemAdmins();
  }, []);

  const fetchSystemAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('system_admins')
        .select('email')
        .order('created_at', { ascending: true });

      if (!error && data) {
        // Combine hardcoded admins with database admins
        const dbAdmins = data.map(admin => admin.email);
        const allAdmins = [...new Set([...SYSTEM_ADMINS, ...dbAdmins])];
        setSystemAdmins(allAdmins);
      }
    } catch (error) {
      console.error('Error fetching system admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const addAdmin = async () => {
    if (newAdmin && !systemAdmins.includes(newAdmin)) {
      try {
        // Add to system_admins table
        const { error } = await supabase
          .from('system_admins')
          .insert({
            email: newAdmin,
            created_by: user?.email || 'Admin'
          });

        if (error) {
          console.error('Error adding admin:', error);
          return;
        }

        await fetchSystemAdmins(); // Refresh the list
        setNewAdmin('');
      } catch (error) {
        console.error('Error adding admin:', error);
      }
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">System Administrators</h2>
      
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          These users have full access to the admin console and all system features.
        </p>
        
        <div className="flex gap-2 mb-4">
          <input
            type="email"
            value={newAdmin}
            onChange={(e) => setNewAdmin(e.target.value)}
            placeholder="admin@example.com"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
          />
          <button
            onClick={addAdmin}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add Admin
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {systemAdmins.map((email, index) => (
            <li key={index} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{email}</span>
                {index === 0 && (
                  <span className="text-xs text-gray-500">Primary Admin</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Admins added here are stored in the database. The hardcoded 
          SYSTEM_ADMINS array provides fallback access in case of database issues.
        </p>
      </div>
    </div>
  );
}